import {
  call,
  delay,
  put,
  race,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { PayloadAction } from 'typesafe-actions';

import {
  lookupAddressBySearchTermFailure,
  lookupAddressBySearchTermSuccess,
  retrieveAddressFailure,
  retrieveAddressSuccess,
} from './validationActions';
import { ActionType, SagaConfig } from './validationTypes';

import { Req } from '@payaca/helpers/storeHelper';

import { AccountRegions } from '@payaca/types/accountTypes';
import { DEFAULT_API_REQUEST_TIMEOUT_MS } from '../constants';

const userSagaCreator = ({
  apiBaseurl,
  getAuthHeader,
  isNativeApp = false,
}: SagaConfig) => {
  const req = Req(`${apiBaseurl}/api`, getAuthHeader, isNativeApp);

  function* handleLookupAddressBySearchTerm(
    action: PayloadAction<
      ActionType.REQUEST_LOOKUP_ADDRESS_BY_SEARCH_TERM,
      {
        searchTerm: string;
        containerId: string;
        region: AccountRegions;
        callback: (error: any, isValid: boolean) => void;
      }
    >
  ) {
    try {
      const { response, timeout } = yield race({
        response: call(
          lookupAddressBySearchTerm,
          action.payload.searchTerm,
          action.payload.containerId,
          action.payload.region
        ),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          lookupAddressBySearchTermFailure(
            new Error('Lookup postcode timed out.')
          )
        );
      } else {
        yield put(lookupAddressBySearchTermSuccess(response));
        action.payload.callback(null, response);
      }
    } catch (error: any) {
      yield put(lookupAddressBySearchTermFailure(error));
    }
  }
  const lookupAddressBySearchTerm = async (
    searchTerm: string,
    containerId: string,
    region: AccountRegions
  ) => {
    const response = await req.get(
      `/addresses/lookup?searchTerm=${encodeURIComponent(
        searchTerm
      )}&region=${region}&containerId=${containerId}`
    );
    return await response.json();
  };

  function* handleRetrieveAddress(
    action: PayloadAction<
      ActionType.REQUEST_RETRIEVE_ADDRESS,
      {
        addressId: string;
        callback: (error: any, isValid: boolean) => void;
      }
    >
  ) {
    try {
      const { response, timeout } = yield race({
        response: call(lookupAddress, action.payload.addressId),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          retrieveAddressFailure(new Error('Lookup address timed out.'))
        );
      } else {
        yield put(retrieveAddressSuccess());
        action.payload.callback(null, response.address);
      }
    } catch (error: any) {
      yield put(retrieveAddressFailure(error));
    }
  }
  const lookupAddress = async (addressId: string) => {
    const response = await req.get(`/addresses/retrieve?id=${addressId}`);
    return await response.json();
  };

  return function* () {
    yield takeLatest(
      ActionType.REQUEST_LOOKUP_ADDRESS_BY_SEARCH_TERM,
      handleLookupAddressBySearchTerm
    );
    yield takeEvery(ActionType.REQUEST_RETRIEVE_ADDRESS, handleRetrieveAddress);
  };
};

export default userSagaCreator;
